import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import opensearchService from '../services/opensearchService';
import './OpenSearchAdmin.css';

const STATUS_COLOUR = { green: '#16a34a', yellow: '#d97706', red: '#dc2626' };
const DEFAULT_PATTERN = 'cwlogs-*';
const LOG_COLS = ['@timestamp', 'logGroup', 'logStream', 'message'];
const LOG_SIZE = 50;

const fmt = (n) => n != null ? Number(n).toLocaleString() : '-';
const fmtBytes = (str) => str || '-';
const truncate = (str, n = 120) => str && str.length > n ? str.slice(0, n) + '…' : (str || '');

const HealthBadge = ({ status }) => (
    <span className="os-health-badge" style={{ background: status === 'green' ? '#dcfce7' : status === 'yellow' ? '#fef9c3' : '#fee2e2', color: STATUS_COLOUR[status] || '#374151' }}>
        {status?.toUpperCase() || 'UNKNOWN'}
    </span>
);

export default function OpenSearchAdmin() {
    const navigate = useNavigate();
    const [health, setHealth] = useState(null);
    const [indices, setIndices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [msg, setMsg] = useState(null);

    // Log search
    const [inputPattern, setInputPattern] = useState(DEFAULT_PATTERN);
    const [activePattern, setActivePattern] = useState(DEFAULT_PATTERN);
    const [inputLogQ, setInputLogQ] = useState('');
    const [logQ, setLogQ] = useState('');
    const [logDocs, setLogDocs] = useState([]);
    const [logTotal, setLogTotal] = useState(0);
    const [logFrom, setLogFrom] = useState(0);
    const [logLoading, setLogLoading] = useState(false);
    const [logError, setLogError] = useState(null);
    const [logExpanded, setLogExpanded] = useState(null);
    const debounceLogQ = useRef(null);

    // LogGroup searchable dropdown
    const [logGroupOptions, setLogGroupOptions] = useState([]);
    const [logGroupFilter, setLogGroupFilter] = useState('');
    const [logGroupInput, setLogGroupInput] = useState('');
    const [logGroupOpen, setLogGroupOpen] = useState(false);
    const logGroupRef = useRef(null);

    // LogStream searchable dropdown
    const [logStreamOptions, setLogStreamOptions] = useState([]);
    const [logStreamFilter, setLogStreamFilter] = useState('');
    const [logStreamInput, setLogStreamInput] = useState('');
    const [logStreamOpen, setLogStreamOpen] = useState(false);
    const logStreamRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [hRes, iRes] = await Promise.all([
                opensearchService.getHealth(),
                opensearchService.listIndices(),
            ]);
            setHealth(hRes.data);
            setIndices(iRes.data || []);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, []);

    const searchLogs = useCallback(async (pattern, q, from, logStream, logGroup) => {
        if (!pattern) return;
        setLogLoading(true);
        setLogError(null);
        try {
            const res = await opensearchService.searchDocs(pattern, {
                q, size: LOG_SIZE, from,
                logStream: logStream || undefined,
                logGroup: logGroup || undefined,
            });
            setLogDocs(res.data.hits || []);
            setLogTotal(res.data.total || 0);
        } catch (e) {
            setLogError(e.response?.data?.message || e.message || 'Search failed');
            setLogDocs([]);
            setLogTotal(0);
        } finally {
            setLogLoading(false);
        }
    }, []);

    const loadLogGroupOptions = useCallback(async (pattern) => {
        if (!pattern) return;
        try {
            const res = await opensearchService.getAggs(pattern, 'logGroup.keyword');
            setLogGroupOptions(res.data.values || []);
        } catch {
            setLogGroupOptions([]);
        }
    }, []);

    const loadLogStreamOptions = useCallback(async (pattern) => {
        if (!pattern) return;
        try {
            const res = await opensearchService.getAggs(pattern, 'logStream.keyword');
            setLogStreamOptions(res.data.values || []);
        } catch {
            setLogStreamOptions([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        searchLogs(activePattern, logQ, logFrom, logStreamFilter, logGroupFilter);
    }, [searchLogs, activePattern, logQ, logFrom, logStreamFilter, logGroupFilter]);

    useEffect(() => {
        loadLogGroupOptions(activePattern);
        loadLogStreamOptions(activePattern);
        setLogGroupFilter('');
        setLogGroupInput('');
        setLogStreamFilter('');
        setLogStreamInput('');
    }, [loadLogGroupOptions, loadLogStreamOptions, activePattern]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e) => {
            if (logGroupRef.current && !logGroupRef.current.contains(e.target)) setLogGroupOpen(false);
            if (logStreamRef.current && !logStreamRef.current.contains(e.target)) setLogStreamOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleDelete = async (indexName) => {
        setDeleting(indexName);
        setMsg(null);
        try {
            await opensearchService.deleteIndex(indexName);
            setMsg({ type: 'success', text: `Index "${indexName}" deleted` });
            setConfirmDelete(null);
            load();
        } catch (e) {
            setMsg({ type: 'error', text: e.response?.data?.message || e.message || 'Delete failed' });
        } finally {
            setDeleting(null);
        }
    };

    const handleLogQ = (val) => {
        setInputLogQ(val);
        clearTimeout(debounceLogQ.current);
        debounceLogQ.current = setTimeout(() => { setLogFrom(0); setLogQ(val); }, 400);
    };

    const applyPattern = () => {
        setActivePattern(inputPattern.trim() || DEFAULT_PATTERN);
        setLogFrom(0);
        setLogGroupFilter('');
        setLogGroupInput('');
        setLogStreamFilter('');
        setLogStreamInput('');
    };

    const selectLogGroup = (val) => {
        setLogGroupFilter(val);
        setLogGroupInput(val);
        setLogGroupOpen(false);
        setLogFrom(0);
    };

    const clearLogGroup = () => {
        setLogGroupFilter('');
        setLogGroupInput('');
        setLogFrom(0);
    };

    const selectLogStream = (val) => {
        setLogStreamFilter(val);
        setLogStreamInput(val);
        setLogStreamOpen(false);
        setLogFrom(0);
    };

    const clearLogStream = () => {
        setLogStreamFilter('');
        setLogStreamInput('');
        setLogFrom(0);
    };

    const filteredGroupOptions = logGroupOptions.filter(o =>
        !logGroupInput || o.toLowerCase().includes(logGroupInput.toLowerCase())
    );

    const filteredStreamOptions = logStreamOptions.filter(o =>
        !logStreamInput || o.toLowerCase().includes(logStreamInput.toLowerCase())
    );

    const logPages = Math.ceil(logTotal / LOG_SIZE);
    const logPage  = Math.floor(logFrom / LOG_SIZE);

    return (
        <div className="acp-page" style={{ maxWidth: 1200 }}>
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">OpenSearch</h1>
                    <p className="acp-subtitle">Cluster health, log search and index management</p>
                </div>
                <button className="acp-btn acp-btn-secondary" onClick={load} disabled={loading}>
                    {loading ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            {msg && <div className={`os-msg os-msg-${msg.type}`}>{msg.text}</div>}
            {error && <div className="os-msg os-msg-error">{error}</div>}

            {health && (
                <div className="acp-card os-health-card">
                    <div className="acp-card-header">
                        <span className="acp-card-title">Cluster Health</span>
                        <HealthBadge status={health.status} />
                    </div>
                    <div className="os-health-grid">
                        <div className="os-stat"><span className="os-stat-label">Cluster</span><span className="os-stat-value">{health.cluster_name}</span></div>
                        <div className="os-stat"><span className="os-stat-label">Nodes</span><span className="os-stat-value">{health.number_of_nodes}</span></div>
                        <div className="os-stat"><span className="os-stat-label">Shards</span><span className="os-stat-value">{health.active_shards}</span></div>
                        <div className="os-stat"><span className="os-stat-label">Unassigned</span><span className="os-stat-value" style={{ color: health.unassigned_shards > 0 ? '#d97706' : undefined }}>{health.unassigned_shards}</span></div>
                        <div className="os-stat"><span className="os-stat-label">Relocating</span><span className="os-stat-value">{health.relocating_shards}</span></div>
                        <div className="os-stat"><span className="os-stat-label">Initialising</span><span className="os-stat-value">{health.initializing_shards}</span></div>
                        <div className="os-stat"><span className="os-stat-label">Pending tasks</span><span className="os-stat-value">{health.number_of_pending_tasks}</span></div>
                        <div className="os-stat"><span className="os-stat-label">Active primary</span><span className="os-stat-value">{health.active_primary_shards}</span></div>
                    </div>
                </div>
            )}

            {/* ── Log Search ── */}
            <div className="acp-card">
                <div className="acp-card-header">
                    <span className="acp-card-title">Log Search</span>
                    {logLoading && <span className="os-loading-dot">Searching…</span>}
                    {!logLoading && logTotal > 0 && <span className="os-doc-count">{fmt(logTotal)} documents</span>}
                </div>

                <div className="os-log-search-bar">
                    <div className="os-log-pattern-wrap">
                        <label className="os-filter-label">Index pattern</label>
                        <div className="os-pattern-input-row">
                            <input
                                className="acp-input os-pattern-input"
                                value={inputPattern}
                                onChange={e => setInputPattern(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && applyPattern()}
                                placeholder="e.g. cwlogs-*"
                                spellCheck={false}
                            />
                        </div>
                    </div>

                    {/* Log group searchable dropdown */}
                    <div className="os-log-stream-wrap" ref={logGroupRef}>
                        <label className="os-filter-label">Log group</label>
                        <div className="os-stream-dropdown">
                            <div className="os-stream-input-row">
                                <input
                                    className="acp-input os-stream-input"
                                    value={logGroupInput}
                                    onChange={e => { setLogGroupInput(e.target.value); setLogGroupOpen(true); if (!e.target.value) clearLogGroup(); }}
                                    onFocus={() => setLogGroupOpen(true)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && filteredGroupOptions.length > 0) selectLogGroup(filteredGroupOptions[0]);
                                        if (e.key === 'Escape') setLogGroupOpen(false);
                                    }}
                                    placeholder="All log groups…"
                                />
                                {logGroupFilter && (
                                    <button className="os-stream-clear-x" onClick={clearLogGroup} title="Clear">✕</button>
                                )}
                            </div>
                            {logGroupOpen && filteredGroupOptions.length > 0 && (
                                <ul className="os-stream-list">
                                    {filteredGroupOptions.slice(0, 50).map(o => (
                                        <li key={o} className={`os-stream-option${logGroupFilter === o ? ' selected' : ''}`} onMouseDown={() => selectLogGroup(o)}>
                                            {o}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Log stream searchable dropdown */}
                    <div className="os-log-stream-wrap" ref={logStreamRef}>
                        <label className="os-filter-label">Log stream</label>
                        <div className="os-stream-dropdown">
                            <div className="os-stream-input-row">
                                <input
                                    className="acp-input os-stream-input"
                                    value={logStreamInput}
                                    onChange={e => { setLogStreamInput(e.target.value); setLogStreamOpen(true); if (!e.target.value) clearLogStream(); }}
                                    onFocus={() => setLogStreamOpen(true)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && filteredStreamOptions.length > 0) selectLogStream(filteredStreamOptions[0]);
                                        if (e.key === 'Escape') setLogStreamOpen(false);
                                    }}
                                    placeholder="All log streams…"
                                />
                                {logStreamFilter && (
                                    <button className="os-stream-clear-x" onClick={clearLogStream} title="Clear">✕</button>
                                )}
                            </div>
                            {logStreamOpen && filteredStreamOptions.length > 0 && (
                                <ul className="os-stream-list">
                                    {filteredStreamOptions.slice(0, 50).map(o => (
                                        <li key={o} className={`os-stream-option${logStreamFilter === o ? ' selected' : ''}`} onMouseDown={() => selectLogStream(o)}>
                                            {o}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="os-log-q-wrap">
                        <label className="os-filter-label">Search</label>
                        <input
                            className="acp-input os-log-q-input"
                            value={inputLogQ}
                            onChange={e => handleLogQ(e.target.value)}
                            placeholder="Search messages, log groups…"
                        />
                    </div>
                    {(logQ || logGroupFilter || logStreamFilter || activePattern !== DEFAULT_PATTERN) && (
                        <button className="acp-btn acp-btn-secondary acp-btn-sm os-log-clear-btn" onClick={() => {
                            setInputLogQ(''); setLogQ('');
                            setInputPattern(DEFAULT_PATTERN); setActivePattern(DEFAULT_PATTERN);
                            setLogGroupFilter(''); setLogGroupInput('');
                            setLogStreamFilter(''); setLogStreamInput('');
                            setLogFrom(0);
                        }}>
                            Clear all
                        </button>
                    )}
                </div>

                {logError && <div className="os-msg os-msg-error" style={{ margin: '0.75rem 1.25rem' }}>{logError}</div>}

                {logDocs.length === 0 && !logLoading ? (
                    <div className="acp-empty">{logQ ? 'No results' : 'No documents found'}</div>
                ) : (
                    <>
                        <div className="os-doc-table-wrap">
                            <table className="acp-table os-log-table">
                                <thead>
                                    <tr>
                                        {LOG_COLS.map(c => <th key={c}>{c}</th>)}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logDocs.map((d) => {
                                        const s = d.source || {};
                                        const isExp = logExpanded === d.id;
                                        return (
                                            <React.Fragment key={d.id}>
                                                <tr
                                                    className={isExp ? 'os-doc-row-expanded' : ''}
                                                    onClick={() => setLogExpanded(isExp ? null : d.id)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td className="os-log-ts">{s['@timestamp'] ? new Date(s['@timestamp']).toLocaleString() : ''}</td>
                                                    <td className="os-log-group">{s.logGroup || ''}</td>
                                                    <td className="os-log-stream">{s.logStream || ''}</td>
                                                    <td className="os-log-msg">{truncate(s.message)}</td>
                                                    <td><span className="os-expand-icon">{isExp ? '▲' : '▼'}</span></td>
                                                </tr>
                                                {isExp && (
                                                    <tr className="os-doc-json-row">
                                                        <td colSpan={LOG_COLS.length + 1}>
                                                            <pre className="os-doc-json">{JSON.stringify(s, null, 2)}</pre>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {logPages > 1 && (
                            <div className="os-pagination">
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage === 0} onClick={() => setLogFrom(0)}>«</button>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage === 0} onClick={() => setLogFrom(Math.max(0, logFrom - LOG_SIZE))}>‹</button>
                                <span className="os-page-info">Page {logPage + 1} of {logPages}</span>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage >= logPages - 1} onClick={() => setLogFrom(logFrom + LOG_SIZE)}>›</button>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage >= logPages - 1} onClick={() => setLogFrom((logPages - 1) * LOG_SIZE)}>»</button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Indices ── */}
            <div className="acp-card">
                <div className="acp-card-header">
                    <span className="acp-card-title">Indices ({indices.length})</span>
                </div>
                {loading && indices.length === 0 ? (
                    <div className="acp-empty">Loading...</div>
                ) : indices.length === 0 ? (
                    <div className="acp-empty">No indices found</div>
                ) : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Index</th>
                                <th>Status</th>
                                <th>Health</th>
                                <th>Docs</th>
                                <th>Size</th>
                                <th>Shards</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {indices.map((idx) => (
                                <tr key={idx.index}>
                                    <td>
                                        <button className="os-index-link" onClick={() => navigate(`/admin/opensearch/indices/${encodeURIComponent(idx.index)}`)}>
                                            {idx.index}
                                        </button>
                                    </td>
                                    <td><span className={`os-status-pill os-status-${idx.status}`}>{idx.status}</span></td>
                                    <td><HealthBadge status={idx.health} /></td>
                                    <td>{fmt(idx['docs.count'])}</td>
                                    <td>{fmtBytes(idx['store.size'])}</td>
                                    <td>{idx.pri} / {idx.rep}</td>
                                    <td>
                                        {confirmDelete === idx.index ? (
                                            <div className="os-confirm-row">
                                                <span className="os-confirm-text">Delete?</span>
                                                <button className="acp-btn acp-btn-danger acp-btn-sm" onClick={() => handleDelete(idx.index)} disabled={deleting === idx.index}>
                                                    {deleting === idx.index ? 'Deleting...' : 'Confirm'}
                                                </button>
                                                <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setConfirmDelete(null)}>Cancel</button>
                                            </div>
                                        ) : (
                                            <button className="acp-btn acp-btn-danger acp-btn-sm" onClick={() => { setConfirmDelete(idx.index); setMsg(null); }} disabled={!!deleting}>
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
